"use client";

/**
 * 设置面板。聊天页的设置弹窗和 /settings 页共用这一份，
 * 避免同一个设置在两个地方各写一遍、日子久了两边行为不一致。
 *
 * 改密码用内联展开的表单而不是再套一个 Dialog——面板会被塞进弹窗里，
 * 弹窗套弹窗在焦点管理和 ESC 关闭上很容易出怪问题。
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { Check, Download, Loader2, Mic, Pencil, Trash2, Volume2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import type { z } from "zod";

import { convertConversations, readConversationsFromZip } from "@/lib/deepseek-import";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiFetch, authApi, ensureFreshAccessToken } from "@/lib/api";
import { getDeviceId } from "@/lib/device";
import { useAuthStore } from "@/lib/auth-store";
import { changePasswordSchema } from "@/lib/schemas";
import { conversationsApi, sharesApi, type ShareRow } from "@/lib/conversations";
import { previewVoice, speakText, stopVoicePreview, ttsPlayer } from "@/lib/tts-player";
import { setSelectedVoice, TTS_PREVIEW_TEXT } from "@/lib/tts-voice";
import { downloadVoiceAudio, voicesApi, type ServerVoice } from "@/lib/voices-api";
import { VoiceRecorderDialog } from "@/components/voice-recorder-dialog";
import { cn } from "@/lib/utils";

type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

function maskPhone(phone: string) {
  if (phone.length !== 11) return phone;
  return `${phone.slice(0, 3)}****${phone.slice(-4)}`;
}

function maskEmail(email: string) {
  const [name, domain] = email.split("@");
  if (!domain) return email;
  if (name.length <= 4) return `${name[0]}***${name.slice(-1)}@${domain}`;
  return `${name.slice(0, 2)}****${name.slice(-2)}@${domain}`;
}

const PROVIDER_LABEL: Record<string, string> = { wechat: "微信", apple: "Apple" };

/** 头像。跟侧边栏 AccountPanel 用同一个数据源（/api/auth/me 的 avatar_url，
 *  data URI），所以微信登录带回来的那张在设置里和侧边栏必然一致。
 *  没有头像的（手机号/邮箱注册）退回首字母色块，跟侧边栏的降级一致。 */
function ProfileAvatar({ url, fallback }: { url?: string | null; fallback?: string | null }) {
  if (url) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- data URI，没有远程加载，用不上 next/image
      <img src={url} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
    );
  }
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
      {(fallback ?? "").slice(0, 2).toUpperCase() || "—"}
    </span>
  );
}

/** 改用户名。行内编辑，不弹窗——一个输入框不值得再套一层。 */
function NicknameRow() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    const next = value.trim();
    if (!next) {
      toast.error("用户名不能为空");
      return;
    }
    setSaving(true);
    try {
      const updated = await authApi.updateProfile(next);
      setUser(updated);
      setEditing(false);
      toast.success("用户名已更新");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <Field
        label="用户名"
        value={user?.nickname || "未设置"}
        leading={<ProfileAvatar url={user?.avatar_url} fallback={user?.nickname} />}
        action={
          <button
            type="button"
            className="text-sm text-idea-accent hover:underline"
            onClick={() => {
              setValue(user?.nickname ?? "");
              setEditing(true);
            }}
          >
            修改
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">用户名</p>
      <div className="flex items-center gap-2">
        <Input
          value={value}
          maxLength={24}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") setEditing(false);
          }}
          className="max-w-[16rem]"
        />
        <Button type="button" size="sm" disabled={saving} onClick={() => void save()}>
          {saving ? "保存中…" : "保存"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>
          取消
        </Button>
      </div>
    </div>
  );
}

/** 换绑手机号：验证码发到**新**号。用途与登录码隔离，一张码顶不了两个流程。 */
function PhoneRow() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);

  // 倒计时：后端同号 60s 一条，按钮不锁住用户只会白点然后吃 429
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function sendCode() {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      toast.error("请输入正确的手机号");
      return;
    }
    setBusy(true);
    try {
      const res = await authApi.sendPhoneChangeCode(phone);
      setCooldown(res.retry_after || 60);
      toast.success("验证码已发送");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "发送失败");
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    try {
      const updated = await authApi.changePhone(phone, code);
      setUser(updated);
      setOpen(false);
      setPhone("");
      setCode("");
      toast.success("手机号已变更");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "变更失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <Field
        label="手机号"
        value={user?.phone ? maskPhone(user.phone) : "未绑定"}
        action={
          <button
            type="button"
            className="text-sm text-idea-accent hover:underline"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "取消" : user?.phone ? "变更" : "绑定"}
          </button>
        }
      />
      {open ? (
        <div className="space-y-3 rounded-2xl border p-4">
          <div className="space-y-2">
            <Label htmlFor="new-phone">新手机号</Label>
            <div className="flex items-center gap-2">
              <Input
                id="new-phone"
                inputMode="numeric"
                maxLength={11}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                className="max-w-[14rem]"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy || cooldown > 0}
                onClick={() => void sendCode()}
              >
                {cooldown > 0 ? `${cooldown}s` : "获取验证码"}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone-code">验证码</Label>
            <Input
              id="phone-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="max-w-[10rem]"
            />
          </div>
          <Button type="button" disabled={busy || !phone || !code} onClick={() => void submit()}>
            {busy ? "提交中…" : "确认变更"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** 第三方绑定。列表来自服务端，不硬编码——以后加 Apple 登录不用改这里。 */
function IdentitiesRows() {
  const [rows, setRows] = useState<{ provider: string; created_at: string }[] | null>(null);

  async function load() {
    try {
      setRows(await authApi.identities());
    } catch {
      setRows([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function unbind(provider: string) {
    try {
      await authApi.unbindIdentity(provider);
      await load();
      toast.success(`已解绑${PROVIDER_LABEL[provider] ?? provider}`);
    } catch (error) {
      // 后端会拦「解绑后就没有登录方式了」这种情况，把原话透给用户
      toast.error(error instanceof Error ? error.message : "解绑失败");
    }
  }

  if (rows === null || rows.length === 0) return null;

  return (
    <>
      {rows.map((r) => (
        <Field
          key={r.provider}
          label={PROVIDER_LABEL[r.provider] ?? r.provider}
          value="已绑定"
          action={
            <button
              type="button"
              className="text-sm text-destructive hover:underline"
              onClick={() => void unbind(r.provider)}
            >
              解绑
            </button>
          }
        />
      ))}
    </>
  );
}

/** 注销账号。二次确认 + 短信验证码——不可逆操作不能只隔一个按钮。 */
function DangerZone() {
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function logoutAll() {
    try {
      await authApi.revokeAllSessions();
      clearSession();
      toast.success("已登出所有设备");
      window.location.href = "/login";
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "操作失败");
    }
  }

  async function sendCode() {
    if (!user?.phone) return;
    try {
      const res = await authApi.sendSms(user.phone);
      setCooldown(res.retry_after || 60);
      toast.success("验证码已发送到当前手机号");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "发送失败");
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await authApi.deleteAccount(code);
      clearSession();
      toast.success("账号已注销");
      window.location.href = "/";
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "注销失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 border-t pt-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-base">登出所有设备</p>
          <p className="text-xs text-muted-foreground">
            包括当前这台，之后需要重新登录
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void logoutAll()}>
          登出
        </Button>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-base">注销账号</p>
          <p className="text-xs text-muted-foreground">
            账号立即失效，30 天后彻底删除数据，期间无法恢复
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-destructive"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "取消" : "注销"}
        </Button>
      </div>

      {open ? (
        <div className="space-y-3 rounded-2xl border border-destructive/40 p-4">
          {user?.phone ? (
            <>
              <p className="text-sm">
                注销后 <strong>{maskPhone(user.phone)}</strong> 可立即重新注册，但当前账号的
                对话与数据不再可用。请输入验证码确认。
              </p>
              <div className="flex items-center gap-2">
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  placeholder="验证码"
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="max-w-[10rem]"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={cooldown > 0}
                  onClick={() => void sendCode()}
                >
                  {cooldown > 0 ? `${cooldown}s` : "获取验证码"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={busy || !code}
                  onClick={() => void doDelete()}
                >
                  {busy ? "注销中…" : "确认注销"}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              当前账号未绑定手机号，无法验证身份。请先绑定手机号后再注销。
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function AccountPanel() {
  const user = useAuthStore((s) => s.user);
  const [pwOpen, setPwOpen] = useState(false);

  const passwordForm = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: "", password: "", confirmPassword: "" }
  });

  async function changePassword(values: ChangePasswordValues) {
    try {
      await apiFetch<void>("/api/auth/password", {
        method: "PATCH",
        body: JSON.stringify({
          current_password: values.currentPassword,
          password: values.password
        })
      });
      passwordForm.reset();
      setPwOpen(false);
      toast.success("密码已更新");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "密码更新失败");
    }
  }

  return (
    <div className="space-y-6">
      <NicknameRow />
      {/* 没绑邮箱就整行不显示——「未绑定」既不可点也没信息量 */}
      {user?.email ? <Field label="邮箱" value={maskEmail(user.email)} /> : null}
      <PhoneRow />
      <IdentitiesRows />

      {/* 手机号登录的用户从来没设过密码，显示"••••••••  修改"是骗人的 */}
      {user?.email ? (
        <div className="space-y-3">
          <Field
            label="密码"
            value="••••••••"
            action={
              <button
                type="button"
                className="text-sm text-idea-accent hover:underline"
                onClick={() => setPwOpen((v) => !v)}
              >
                {pwOpen ? "取消" : "修改"}
              </button>
            }
          />
          {pwOpen ? (
            <form className="space-y-4 rounded-2xl border p-4" onSubmit={passwordForm.handleSubmit(changePassword)}>
              <div className="space-y-2">
                <Label htmlFor="current-password">当前密码</Label>
                <Input id="current-password" type="password" autoComplete="current-password" {...passwordForm.register("currentPassword")} />
                {passwordForm.formState.errors.currentPassword ? (
                  <p className="text-sm text-destructive">{passwordForm.formState.errors.currentPassword.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">新密码</Label>
                <Input id="new-password" type="password" autoComplete="new-password" {...passwordForm.register("password")} />
                {passwordForm.formState.errors.password ? (
                  <p className="text-sm text-destructive">{passwordForm.formState.errors.password.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">确认新密码</Label>
                <Input id="confirm-password" type="password" autoComplete="new-password" {...passwordForm.register("confirmPassword")} />
                {passwordForm.formState.errors.confirmPassword ? (
                  <p className="text-sm text-destructive">{passwordForm.formState.errors.confirmPassword.message}</p>
                ) : null}
              </div>
              <Button type="submit" disabled={passwordForm.formState.isSubmitting}>
                {passwordForm.formState.isSubmitting ? "保存中..." : "保存"}
              </Button>
            </form>
          ) : null}
        </div>
      ) : null}

      <DangerZone />
    </div>
  );
}

/** 语音。数据源是 GET /api/voices（服务端），不是 /v1/tts/voices。
 *
 *  两者的区别：/v1/tts/voices 是给 API 使用者看的目录，只有 id/name/desc；
 *  /api/voices 才带 selected/builtin，而且包含用户自己录的音色。
 *
 *  「使用」为什么要打服务端而不是只写 localStorage：选中的音色存在
 *  users.voice_slug，换电脑、换手机、网页转 App 都跟着账号走。localStorage
 *  仍然写一份，但只当本地缓存让下一次朗读立刻生效，不是真相。 */
export function VoicePanel() {
  const [voices, setVoices] = useState<ServerVoice[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameText, setRenameText] = useState("");

  async function load() {
    try {
      const list = await voicesApi.list();
      setVoices(list);
      // 服务端说了算：把本地缓存对齐过来，避免这台设备上还留着旧选择
      const sel = list.find((v) => v.selected);
      if (sel) setSelectedVoice({ id: sel.slug, name: sel.name, desc: sel.desc });
    } catch {
      setFailed(true);
    }
  }

  useEffect(() => {
    void load();
    return () => {
      stopVoicePreview();
      ttsPlayer.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 试听：临时用这个音色念一句，不改用户已保存的选择。
   *
   *  必须走 previewVoice 而不是 speakText，两个原因：
   *  1. 连点会叠声音。speakText 走全局播放器，只停"正在播的"，停不掉"正在合成中"
   *     的那一次——点第二下时第一次还在网络往返里，等它回来照样播，两个声音重叠。
   *     previewVoice 每次先 abort 上一次的请求，连点多少下都只出最后一次的声音。
   *  2. speakText 会把朗读控制条顶出来，试听只是听一句，不该弹出播放条。 */
  function preview(v: ServerVoice) {
    setPreviewing(v.slug);
    void previewVoice(TTS_PREVIEW_TEXT, v.slug)
      .catch(() => undefined)
      .finally(() => setPreviewing((s) => (s === v.slug ? null : s)));
  }

  async function use(v: ServerVoice) {
    if (v.selected) return;
    setBusy(v.slug);
    // 先乐观更新，失败再回滚——一次往返里让整排按钮僵住，手感很差
    const prev = voices;
    setVoices((list) => list?.map((x) => ({ ...x, selected: x.slug === v.slug })) ?? null);
    try {
      await voicesApi.select(v.slug);
      setSelectedVoice({ id: v.slug, name: v.name, desc: v.desc });
      toast.success(`朗读音色已换成「${v.name}」`);
    } catch (e) {
      setVoices(prev);
      toast.error(e instanceof Error ? e.message : "切换失败，请稍后再试");
    } finally {
      setBusy(null);
    }
  }

  async function download(v: ServerVoice) {
    setBusy(v.slug);
    try {
      await downloadVoiceAudio(v.slug, v.name);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "下载失败");
    } finally {
      setBusy(null);
    }
  }

  async function submitRename(v: ServerVoice) {
    const name = renameText.trim();
    setRenaming(null);
    if (!name || name === v.name) return;
    try {
      await voicesApi.rename(v.slug, name);
      setVoices((list) => list?.map((x) => (x.slug === v.slug ? { ...x, name } : x)) ?? null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "改名失败");
    }
  }

  async function destroy(v: ServerVoice) {
    if (!window.confirm(`删除「${v.name}」？这段录音会从服务器上一并删除，不能恢复。`)) return;
    setBusy(v.slug);
    try {
      await voicesApi.destroy(v.slug);
      toast.success(`「${v.name}」已删除`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "删除失败");
    } finally {
      setBusy(null);
    }
  }

  function row(v: ServerVoice) {
    const isBusy = busy === v.slug;
    return (
      <li key={v.slug} className="flex items-center gap-2 px-3 py-3">
        <span className="min-w-0 flex-1">
          {renaming === v.slug ? (
            <Input
              autoFocus
              value={renameText}
              maxLength={32}
              onChange={(e) => setRenameText(e.target.value)}
              onBlur={() => void submitRename(v)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void submitRename(v);
                if (e.key === "Escape") setRenaming(null);
              }}
              className="h-8"
            />
          ) : (
            <>
              <span className="block truncate text-sm font-medium">{v.name}</span>
              <span className="block truncate text-xs text-muted-foreground">{v.desc}</span>
            </>
          )}
        </span>

        {!v.builtin && (
          <>
            <button
              type="button"
              onClick={() => {
                setRenameText(v.name);
                setRenaming(v.slug);
              }}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              aria-label={`重命名 ${v.name}`}
              title="重命名"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => void download(v)}
              disabled={isBusy}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-40"
              aria-label={`下载 ${v.name} 的录音`}
              title="下载我的录音"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => void destroy(v)}
              disabled={isBusy}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
              aria-label={`删除 ${v.name}`}
              title="删除"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => preview(v)}
          className="h-8 shrink-0 gap-1.5 px-3"
        >
          {previewing === v.slug ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Volume2 className="h-3.5 w-3.5" />
          )}
          试听
        </Button>

        <Button
          type="button"
          variant={v.selected ? "secondary" : "default"}
          size="sm"
          disabled={v.selected || isBusy}
          onClick={() => void use(v)}
          className="h-8 w-[4.5rem] shrink-0 gap-1.5 px-3"
        >
          {isBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : v.selected ? (
            <>
              <Check className="h-3.5 w-3.5" />
              使用中
            </>
          ) : (
            "使用"
          )}
        </Button>
      </li>
    );
  }

  const builtin = voices?.filter((v) => v.builtin) ?? [];
  const mine = voices?.filter((v) => !v.builtin) ?? [];

  return (
    <div className="space-y-5">
      {voices === null && !failed ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          加载中…
        </div>
      ) : failed ? (
        <p className="py-10 text-center text-sm text-muted-foreground">暂不可用</p>
      ) : (
        <>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">内置音色</p>
            <ul className="divide-y rounded-2xl border">{builtin.map(row)}</ul>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">自定义音色</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRecorderOpen(true)}
                className="h-8 gap-1.5"
              >
                <Mic className="h-3.5 w-3.5" />
                录制
              </Button>
            </div>
            {mine.length === 0 ? (
              <p className="rounded-2xl border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                还没有自己的音色。录一段 3~30 秒的话，就能用自己的声音朗读。
                <br />
                <span className="text-xs">录音只有你能听到和下载，其他人看不到。</span>
              </p>
            ) : (
              <ul className="divide-y rounded-2xl border">{mine.map(row)}</ul>
            )}
          </div>
        </>
      )}

      <VoiceRecorderDialog
        open={recorderOpen}
        onOpenChange={setRecorderOpen}
        onCreated={() => void load()}
      />
    </div>
  );
}

export function AppearancePanel() {
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">色彩主题</p>
      <ThemePicker />
    </div>
  );
}

/** 数据与信息：导出、清空、本地存储。
 *
 *  没做「共享对话」——服务端没有分享链接这回事，现有的分享是聊天页里的
 *  单会话 PDF 导出。在设置里再放一个入口只会让人以为有服务端分享。 */
/** 一个不依赖第三方组件的开关。项目里没有 Switch，为一个开关引一套依赖不划算。 */
function Toggle({
  checked,
  disabled,
  onChange,
  label
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-idea-accent" : "bg-muted-foreground/30"
      )}
    >
      <span
        className={cn(
          "h-5 w-5 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 ease-out",
          checked ? "translate-x-5" : "translate-x-0"
        )}
      />
    </button>
  );
}

/** 「数据用于优化体验」。
 *
 *  这个开关不是摆设：关掉后 portal 会给上游带 X-Idea-No-Train，
 *  wrapper 落进 audit 的 no_train 列，造语料的脚本据此排除。
 *  链路见 tests/structural/test_no_train_filter.py（在 stone 上）。
 */
function TrainingConsentRow() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [saving, setSaving] = useState(false);
  // 服务端真值优先；字段缺失（老版本 token 里的 user）时按开着算，
  // 与这个开关上线之前的实际状态一致
  const allow = user?.allow_training !== false;

  async function toggle(next: boolean) {
    setSaving(true);
    try {
      const updated = await authApi.updatePrivacy(next);
      setUser(updated);
      toast.success(next ? "已开启" : "已关闭，此后的对话不会用于训练");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-1">
        <p className="text-base">数据用于优化体验</p>
        <p className="text-xs text-muted-foreground">
          允许我们用你的对话内容改进模型。关闭后，此后的对话不会进入训练数据。
          已经产生的历史对话不受影响。
        </p>
      </div>
      <Toggle checked={allow} disabled={saving} onChange={(v) => void toggle(v)} label="数据用于优化体验" />
    </div>
  );
}

/** 我共享的链接。列表只列还有效的——它回答的是「我现在有哪些内容是公开的」。 */
function SharedLinksRow() {
  const [rows, setRows] = useState<ShareRow[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    try {
      setRows(await sharesApi.list());
    } catch {
      setRows([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(row: ShareRow) {
    setBusy(row.id);
    try {
      await sharesApi.revoke(row.id);
      await load();
      toast.success("已撤回，该链接立即失效");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "撤回失败");
    } finally {
      setBusy(null);
    }
  }

  const count = rows?.length ?? 0;

  return (
    <div className="space-y-3 border-t pt-6">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <p className="text-base">我共享的链接</p>
          <p className="text-xs text-muted-foreground">
            {rows === null ? "读取中…" : count === 0 ? "还没有分享过对话" : `${count} 条链接正在公开`}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={count === 0}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "收起" : "管理"}
        </Button>
      </div>

      {open && rows ? (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 rounded-2xl border px-4 py-3">
              <div className="min-w-0 space-y-1">
                <p className="truncate text-sm">{r.title}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleDateString("zh-CN")} · {r.message_count} 条消息 ·
                  被查看 {r.view_count} 次
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="text-sm text-idea-accent hover:underline"
                  onClick={() => {
                    void navigator.clipboard.writeText(`${window.location.origin}/s/${r.token}`);
                    toast.success("链接已复制");
                  }}
                >
                  复制
                </button>
                <button
                  type="button"
                  className="text-sm text-destructive hover:underline disabled:opacity-50"
                  disabled={busy === r.id}
                  onClick={() => void revoke(r)}
                >
                  撤回
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function DataPanel() {
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState<"export" | "clear" | "import" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [localBytes, setLocalBytes] = useState<number | null>(null);
  const [importProgress, setImportProgress] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  function measureLocal() {
    if (typeof window === "undefined") return;
    try {
      let bytes = 0;
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (!k) continue;
        bytes += k.length + (window.localStorage.getItem(k)?.length ?? 0);
      }
      setLocalBytes(bytes * 2); // UTF-16，每字符 2 字节
    } catch {
      setLocalBytes(null);
    }
  }

  async function load() {
    try {
      const res = await conversationsApi.list();
      setCount(res.items.length);
    } catch {
      setCount(null);
    }
  }

  useEffect(() => {
    void load();
    measureLocal();
  }, []);

  async function exportAll() {
    setBusy("export");
    try {
      // 一个请求拿全部。原来是逐个 detail(id) 串行拉，582 个对话就是 582 次
      // 往返，慢到像卡死，中途关标签页就前功尽弃。
      const token = await ensureFreshAccessToken();
      if (!token) throw new Error("登录已过期，请重新登录");
      const res = await fetch("/api/conversations/export", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`导出失败（${res.status}）`);

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deyon-对话导出-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      // revoke 要等浏览器真正开始下载，立刻撤会让 Safari 拿到空文件
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast.success(count ? `已导出 ${count} 个对话` : "已导出");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "导出失败");
    } finally {
      setBusy(null);
    }
  }

  async function clearAll() {
    setBusy("clear");
    try {
      await conversationsApi.clearAll();
      setConfirming(false);
      await load();
      toast.success("已清空全部对话");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "清空失败");
    } finally {
      setBusy(null);
    }
  }

  /** 从 DeepSeek 的数据导出包导入历史对话。
   *
   *  分批提交而不是一次性发全部：真实导出能到 582 个对话 / 3960 条消息，
   *  一个请求打过去既容易超时，失败了也说不清导进去多少。分批还能给出
   *  真实的进度，13MB 的包解析加导入要好几秒，没有进度用户会以为卡死。 */
  async function importDeepSeek(file: File) {
    setBusy("import");
    setImportProgress("正在读取压缩包…");
    try {
      const raw = await readConversationsFromZip(file);
      const { sessions, skippedEmpty, failed } = convertConversations(raw);
      if (sessions.length === 0) {
        toast.error("没有解析出可导入的对话，请确认选的是 DeepSeek 导出的数据包");
        return;
      }

      const BATCH = 50;
      let done = 0;
      try {
        for (let i = 0; i < sessions.length; i += BATCH) {
          await conversationsApi.importLegacy(sessions.slice(i, i + BATCH));
          done += Math.min(BATCH, sessions.length - i);
          setImportProgress(`正在导入 ${done} / ${sessions.length}…`);
        }
      } catch (batchError) {
        // 批与批之间不是原子的：前面成功的批已经落库了。必须把「已导入多少」
        // 说清楚——只报一句「导入失败」的话，用户会重选同一个文件再导一次，
        // 而后端没有去重，那些已经进去的会**再进一份**。
        await load();
        const msg = batchError instanceof Error ? batchError.message : "未知错误";
        toast.error(
          done > 0
            ? `已导入前 ${done} 个对话，之后失败：${msg}。重新导入会让这 ${done} 个重复，建议先清理再试`
            : `导入失败：${msg}`,
          { duration: 12000 }
        );
        return;
      }

      await load();
      const skipped = skippedEmpty + failed;
      toast.success(
        `已导入 ${sessions.length} 个对话` + (skipped > 0 ? `，跳过 ${skipped} 个空对话` : "")
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "导入失败");
    } finally {
      setBusy(null);
      setImportProgress("");
    }
  }

  return (
    <div className="space-y-6">
      <TrainingConsentRow />

      <SharedLinksRow />

      <div className="space-y-1 border-t pt-6">
        <p className="text-sm text-muted-foreground">对话数据</p>
        <p className="text-base">{count === null ? "读取中…" : `共 ${count} 个对话`}</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button type="button" variant="outline" disabled={busy !== null || !count} onClick={exportAll}>
          {busy === "export" ? "导出中…" : "导出全部对话"}
        </Button>
        {!confirming ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy !== null || !count}
            onClick={() => setConfirming(true)}
            className="text-destructive"
          >
            清空全部对话
          </Button>
        ) : (
          <div className="flex items-center gap-2 rounded-2xl border border-destructive/40 px-3 py-2">
            <span className="text-sm">删除后无法恢复，确定？</span>
            <Button type="button" size="sm" variant="outline" onClick={() => setConfirming(false)}>
              取消
            </Button>
            <Button type="button" size="sm" variant="destructive" disabled={busy !== null} onClick={clearAll}>
              {busy === "clear" ? "清空中…" : "确认清空"}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3 border-t pt-6">
        <div className="space-y-1">
          <p className="text-base">从其他 AI 服务商导入</p>
          <p className="text-xs text-muted-foreground">
            只读取其中的对话记录，账号信息不会被导入。
            导入的对话会作为新对话加入，不会覆盖或影响你已有的记录。
          </p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            // 清空 value：不然选同一个文件第二次不会触发 change
            e.target.value = "";
            if (f) void importDeepSeek(f);
          }}
        />
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={busy !== null}
            onClick={() => fileRef.current?.click()}
          >
            {busy === "import" ? "导入中…" : "选择数据包"}
          </Button>
          {importProgress ? (
            <span className="text-xs text-muted-foreground">{importProgress}</span>
          ) : null}
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">本机存储</p>
        <p className="text-base">
          {localBytes === null ? "无法读取" : localBytes < 1024 ? `${localBytes} B` : `${(localBytes / 1024).toFixed(1)} KB`}
        </p>
        <p className="text-xs text-muted-foreground">
          浏览器本地保存的偏好（音色、主题、草稿等）。对话正文存在服务器，不在这里。
        </p>
      </div>
    </div>
  );
}

type SessionRow = {
  id: string;
  device_id: string | null;
  device_name: string | null;
  platform: string | null;
  ip: string | null;
  last_used_at: string;
  created_at: string;
};

function lastUsedLabel(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 2) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 小时前`;
  const day = Math.floor(hour / 24);
  if (day < 30) return `${day} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}

/** 登录设备。不做"新设备顶掉旧设备"，也不设数量上限——
 *  该防的是凭据被盗，不是设备多；让用户自己踢比任何自动策略都可控。 */
export function DevicesPanel() {
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const myDeviceId = typeof window === "undefined" ? "" : getDeviceId();

  async function load() {
    try {
      setRows(await authApi.sessions());
    } catch {
      setRows([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(row: SessionRow) {
    setBusy(row.id);
    try {
      await authApi.revokeSession(row.id);
      toast.success("已登出该设备");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "操作失败");
    } finally {
      setBusy(null);
    }
  }

  async function revokeOthers() {
    setBusy("all");
    try {
      await authApi.revokeOtherSessions();
      toast.success("已登出其他设备");
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "操作失败");
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">登录设备</p>
        <p className="text-sm text-muted-foreground">加载中…</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">登录设备</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">暂无记录</p>
      ) : (
        <ul className="divide-y rounded-2xl border">
          {rows.map((row) => {
            const isCurrent = Boolean(row.device_id) && row.device_id === myDeviceId;
            return (
              <li key={row.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    {row.device_name ?? "未知设备"}
                    {isCurrent ? <span className="ml-2 text-xs text-idea-accent">当前设备</span> : null}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[row.platform === "ios" ? "App" : row.platform === "web" ? "网页" : row.platform, row.ip]
                      .filter(Boolean)
                      .join(" · ")}
                    {" · "}
                    {lastUsedLabel(row.last_used_at)}
                  </p>
                </div>
                {isCurrent ? null : (
                  <button
                    type="button"
                    disabled={busy === row.id}
                    onClick={() => revoke(row)}
                    className="shrink-0 text-sm text-destructive hover:underline disabled:opacity-50"
                  >
                    退出
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {rows.length > 1 ? (
        <Button type="button" variant="outline" disabled={busy === "all"} onClick={revokeOthers}>
          登出其他设备
        </Button>
      ) : null}
    </div>
  );
}

/** 色彩主题。走 next-themes，与聊天页共用同一份状态 */
function ThemePicker() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const mode = !mounted ? "auto" : theme === "system" || !theme ? "auto" : (theme as "light" | "dark");

  function pick(next: "auto" | "light" | "dark") {
    setTheme(next === "auto" ? "system" : next);
  }

  const options = [
    { id: "auto" as const, label: "跟随系统" },
    { id: "light" as const, label: "浅色" },
    { id: "dark" as const, label: "深色" }
  ];

  return (
    <div className="inline-flex rounded-2xl border p-1">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => pick(o.id)}
          className={
            "rounded-xl px-4 py-1.5 text-sm transition-colors " +
            (mode === o.id ? "bg-secondary font-medium text-foreground" : "text-muted-foreground hover:text-foreground")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({
  label,
  value,
  action,
  hint,
  leading
}: {
  label: string;
  value: string;
  action?: React.ReactNode;
  hint?: string;
  /** 值前面的图示（头像之类）。给 ReactNode 而不是 src，免得 Field 去关心它是图还是色块 */
  leading?: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="flex items-center gap-3">
        {leading}
        <p className="text-base">{value}</p>
        {action}
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
